import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { 
    Users, 
    Plus, 
    MoreVertical, 
    Shield, 
    ShoppingCart, 
    Trash2,
    Loader2,
    Mail,
    Lock,
    User,
    Key
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UsersPage = () => {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'vendedor'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/users`);
            setUsers(response.data);
        } catch (error) {
            toast.error('Erro ao carregar usuários');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            role: 'vendedor'
        });
        setDialogOpen(true);
    };

    const handleCreate = async () => {
        if (!formData.name || !formData.email || !formData.password) {
            toast.error('Preencha todos os campos');
            return;
        }

        setSaving(true);

        try {
            await axios.post(`${API_URL}/api/auth/register`, formData);
            toast.success('Usuário criado!');
            setDialogOpen(false);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
        } finally {
            setSaving(false);
        }
    };

    const updateRole = async (userId, newRole) => {
        try {
            await axios.put(`${API_URL}/api/users/${userId}/role?role=${newRole}`);
            toast.success('Função atualizada!');
            fetchUsers();
        } catch (error) {
            toast.error('Erro ao atualizar função');
        }
    };

    const deleteUser = async (user) => {
        if (!window.confirm(`Excluir usuário "${user.name}"?`)) return;

        try {
            await axios.delete(`${API_URL}/api/users/${user.id}`);
            toast.success('Usuário excluído!');
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
        }
    };

    const openPasswordDialog = (user) => {
        setSelectedUser(user);
        setNewPassword('');
        setConfirmPassword('');
        setPasswordDialogOpen(true);
    };

    const handleChangePassword = async () => {
        if (!newPassword) {
            toast.error('Digite a nova senha');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('A senha deve ter pelo menos 6 caracteres');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('As senhas não coincidem');
            return;
        }

        setChangingPassword(true);

        try {
            await axios.put(`${API_URL}/api/users/${selectedUser.id}/password`, {
                new_password: newPassword
            });
            toast.success(`Senha de ${selectedUser.name} alterada com sucesso!`);
            setPasswordDialogOpen(false);
            setSelectedUser(null);
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
        } finally {
            setChangingPassword(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-rr-yellow-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in-up" data-testid="users-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-heading font-bold text-white">Usuários</h1>
                    <p className="text-gray-400 mt-1">Gerencie os usuários do sistema</p>
                </div>
                <Button
                    className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400 font-bold"
                    onClick={openCreateDialog}
                    data-testid="add-user-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Novo Usuário
                </Button>
            </div>

            {/* Role Legend */}
            <div className="flex gap-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-rr-yellow-500/10 rounded flex items-center justify-center">
                        <Shield className="w-4 h-4 text-rr-yellow-500" />
                    </div>
                    <span className="text-gray-400 text-sm">Admin - Acesso total</span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-500/10 rounded flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-blue-500" />
                    </div>
                    <span className="text-gray-400 text-sm">Vendedor - Apenas PDV</span>
                </div>
            </div>

            {/* Users Table */}
            <Card className="bg-[#121212] border-[#262626]">
                <CardHeader>
                    <CardTitle className="text-white font-heading flex items-center gap-2">
                        <Users className="w-5 h-5 text-rr-yellow-500" />
                        Lista de Usuários
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow className="border-[#262626] hover:bg-transparent">
                                <TableHead className="text-gray-400">Usuário</TableHead>
                                <TableHead className="text-gray-400">Email</TableHead>
                                <TableHead className="text-gray-400">Função</TableHead>
                                <TableHead className="text-gray-400">Cadastro</TableHead>
                                <TableHead className="text-gray-400 w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map(user => (
                                <TableRow 
                                    key={user.id} 
                                    className="border-[#262626] hover:bg-white/5"
                                    data-testid={`user-row-${user.id}`}
                                >
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                user.role === 'admin' ? 'bg-rr-yellow-500/10' : user.role === 'atendente' ? 'bg-emerald-500/10' : 'bg-blue-500/10'
                                            }`}>
                                                <span className={`font-bold ${
                                                    user.role === 'admin' ? 'text-rr-yellow-500' : user.role === 'atendente' ? 'text-emerald-500' : 'text-blue-500'
                                                }`}>
                                                    {user.name.charAt(0).toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">{user.name}</p>
                                                {user.id === currentUser?.id && (
                                                    <span className="text-xs text-rr-yellow-500">(Você)</span>
                                                )}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-gray-400">{user.email}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={user.role}
                                            onValueChange={(value) => updateRole(user.id, value)}
                                            disabled={user.id === currentUser?.id}
                                        >
                                            <SelectTrigger className="w-32 bg-[#0a0a0a] border-[#262626] text-white">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-[#121212] border-[#262626]">
                                                <SelectItem value="admin">
                                                    <div className="flex items-center gap-2">
                                                        <Shield className="w-4 h-4 text-rr-yellow-500" />
                                                        Admin
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="vendedor">
                                                    <div className="flex items-center gap-2">
                                                        <ShoppingCart className="w-4 h-4 text-blue-500" />
                                                        Vendedor
                                                    </div>
                                                </SelectItem>
                                                <SelectItem value="atendente">
                                                    <div className="flex items-center gap-2">
                                                        <ShoppingCart className="w-4 h-4 text-emerald-500" />
                                                        Atendente
                                                    </div>
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell className="text-gray-400">
                                        {formatDate(user.created_at)}
                                    </TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon" className="text-gray-400 hover:text-white">
                                                    <MoreVertical className="w-4 h-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" className="bg-[#121212] border-[#262626]">
                                                <DropdownMenuItem 
                                                    className="text-rr-yellow-500 focus:bg-rr-yellow-500/10 focus:text-gold-400 cursor-pointer"
                                                    onClick={() => openPasswordDialog(user)}
                                                >
                                                    <Key className="w-4 h-4 mr-2" />
                                                    Alterar Senha
                                                </DropdownMenuItem>
                                                {user.id !== currentUser?.id && (
                                                    <DropdownMenuItem 
                                                        className="text-red-500 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                                                        onClick={() => deleteUser(user)}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Excluir
                                                    </DropdownMenuItem>
                                                )}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Create User Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white" data-testid="create-user-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading">Novo Usuário</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="text-gray-400">Nome *</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <Input
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="pl-11 bg-[#121212] border-[#262626] text-white"
                                    placeholder="Nome completo"
                                    data-testid="user-name-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Email *</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <Input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    className="pl-11 bg-[#121212] border-[#262626] text-white"
                                    placeholder="email@exemplo.com"
                                    data-testid="user-email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Senha *</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <Input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                    className="pl-11 bg-[#121212] border-[#262626] text-white"
                                    placeholder="••••••••"
                                    data-testid="user-password-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Função *</Label>
                            <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                                <SelectTrigger className="bg-[#121212] border-[#262626] text-white" data-testid="user-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#121212] border-[#262626]">
                                    <SelectItem value="admin">
                                        <div className="flex items-center gap-2">
                                            <Shield className="w-4 h-4 text-rr-yellow-500" />
                                            Admin - Acesso total
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="vendedor">
                                        <div className="flex items-center gap-2">
                                            <ShoppingCart className="w-4 h-4 text-blue-500" />
                                            Vendedor - PDV, Caixa, Pedidos
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="atendente">
                                        <div className="flex items-center gap-2">
                                            <ShoppingCart className="w-4 h-4 text-emerald-500" />
                                            Atendente - Somente PDV e Caixa
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDialogOpen(false)}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreate}
                            disabled={saving}
                            className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400 font-bold"
                            data-testid="save-user-btn"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Criar Usuário'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-[#262626] text-white" data-testid="change-password-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-heading flex items-center gap-2">
                            <Key className="w-5 h-5 text-rr-yellow-500" />
                            Alterar Senha
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {selectedUser && (
                            <div className="p-3 bg-[#121212] rounded-lg border border-[#262626]">
                                <p className="text-gray-400 text-sm">Alterando senha de:</p>
                                <p className="text-white font-medium">{selectedUser.name}</p>
                                <p className="text-rr-yellow-500 text-sm">{selectedUser.email}</p>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-gray-400">Nova Senha *</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <Input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="pl-11 bg-[#121212] border-[#262626] text-white"
                                    placeholder="Mínimo 6 caracteres"
                                    data-testid="new-password-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-gray-400">Confirmar Senha *</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-11 bg-[#121212] border-[#262626] text-white"
                                    placeholder="Repita a nova senha"
                                    data-testid="confirm-password-input"
                                />
                            </div>
                            {confirmPassword && newPassword !== confirmPassword && (
                                <p className="text-red-500 text-sm">As senhas não coincidem</p>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setPasswordDialogOpen(false);
                                setSelectedUser(null);
                                setNewPassword('');
                                setConfirmPassword('');
                            }}
                            className="border-[#262626] text-gray-400"
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleChangePassword}
                            disabled={changingPassword || !newPassword || newPassword !== confirmPassword}
                            className="bg-rr-yellow-500 text-black hover:bg-rr-yellow-400 font-bold"
                            data-testid="save-password-btn"
                        >
                            {changingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Alterar Senha'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
